import { create } from 'zustand';
import { settingsService } from '../services/settings/settings.service';
import { localizationService } from '../services/localizationService';
import type {
  SettingsState,
  UserProfile,
  AppPreferences,
  SecuritySettings,
  NotificationPreferences,
  LocationSettings,
} from '../types/settings.types';

interface SettingsStore {
  data: SettingsState | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  loaded: boolean;

  hydrate: () => Promise<void>;
  saveProfile: (patch: Partial<UserProfile>) => Promise<void>;
  savePreferences: (patch: Partial<AppPreferences>) => Promise<void>;
  saveSecurity: (patch: Partial<SecuritySettings>) => Promise<void>;
  revokeSession: (id: string) => Promise<void>;
  saveNotifications: (patch: Partial<NotificationPreferences>) => Promise<void>;
  saveLocation: (patch: Partial<LocationSettings>) => Promise<void>;
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  data: null,
  loading: false,
  saving: false,
  error: null,
  loaded: false,

  hydrate: async () => {
    if (get().loaded || get().loading) return;
    set({ loading: true, error: null });
    try {
      const data = await settingsService.getSettings();
      set({ data, loading: false, loaded: true });
      applyAppearance(data.preferences);
    } catch {
      set({ loading: false, error: 'Failed to load settings.' });
    }
  },

  saveProfile: async (patch) => {
    set({ saving: true });
    const profile = await settingsService.updateProfile(patch);
    set((s) => ({ saving: false, data: s.data ? { ...s.data, profile } : s.data }));
  },

  savePreferences: async (patch) => {
    set({ saving: true });
    const preferences = await settingsService.updatePreferences(patch);
    applyAppearance(preferences);
    set((s) => ({ saving: false, data: s.data ? { ...s.data, preferences } : s.data }));
  },

  saveSecurity: async (patch) => {
    set({ saving: true });
    const security = await settingsService.updateSecurity(patch);
    set((s) => ({ saving: false, data: s.data ? { ...s.data, security } : s.data }));
  },

  revokeSession: async (id) => {
    set({ saving: true });
    const security = await settingsService.revokeSession(id);
    set((s) => ({ saving: false, data: s.data ? { ...s.data, security } : s.data }));
  },

  saveNotifications: async (patch) => {
    set({ saving: true });
    const notifications = await settingsService.updateNotifications(patch);
    set((s) => ({ saving: false, data: s.data ? { ...s.data, notifications } : s.data }));
  },

  saveLocation: async (patch) => {
    set({ saving: true });
    const location = await settingsService.updateLocation(patch);
    set((s) => ({ saving: false, data: s.data ? { ...s.data, location } : s.data }));
  },
}));

const FONT_SCALE: Record<AppPreferences['fontScale'], string> = {
  small: '15px',
  medium: '16px',
  large: '18px',
};

/**
 * Apply appearance preferences to the document so they take effect app-wide:
 * theme class, accent color CSS var, base font size, and reduced-motion flag.
 * Also forwards language/region to the localization service so the whole UI
 * (LocaleContext subscribers) switches with the saved preference.
 */
export function applyAppearance(prefs: AppPreferences) {
  localizationService.setLocale({ language: prefs.language, region: prefs.region });
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  const dark = prefs.theme === 'dark' || (prefs.theme === 'system' && prefersDark);
  root.classList.toggle('dark', dark);
  root.dataset.theme = dark ? 'dark' : 'light';

  root.style.setProperty('--accent', prefs.accent);
  root.style.fontSize = FONT_SCALE[prefs.fontScale];
  root.classList.toggle('reduce-motion', prefs.reduceMotion);
}
